import { useState, useEffect, useContext } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import API_BASE_URL from '../../config';
import api from '../../utils/api';
import { AuthContext } from '../../context/AuthContext';

const colors = {
  weight: '#84cc16',
  neck: '#ef4444',
  shoulders: '#3b82f6',
  chest: '#f97316',
  biceps: '#6366f1',
  belly: '#10b981',
  waist: '#8b5cf6',
  hip: '#f43f5e',
  calf: '#14b8a6',
  thighs: '#facc15'
};

const AccordionSection = ({ title, isOpen, onToggle, children }) => (
  <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex justify-between items-center px-6 py-4 font-medium text-left text-white hover:bg-[#2b2b2b] focus:outline-none"
      aria-expanded={isOpen}
    >
      <span>{title}</span>
      <svg
        className={`w-5 h-5 text-lime-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'
          }`}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    {isOpen && <div className="pb-6 pt-0">{children}</div>}
  </div>
);

const HealthStats = () => {



  const parseRecordDate = (dateValue) => {
    if (!dateValue) return new Date(0);
    if (dateValue instanceof Date) return dateValue;

    const dateStr = String(dateValue).trim();
    if (!dateStr) return new Date(0);

    const isoDate = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (isoDate) {
      return new Date(Number(isoDate[1]), Number(isoDate[2]) - 1, Number(isoDate[3]));
    }

    const displayDate = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (displayDate) {
      return new Date(Number(displayDate[3]), Number(displayDate[2]) - 1, Number(displayDate[1]));
    }

    const parsed = new Date(dateStr);
    return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
  };

  const toDateInputValue = (dateValue) => {
    const parsed = parseRecordDate(dateValue);
    if (parsed.getTime() === 0) return '';

    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const toApiDate = (dateValue) => {
    const parsed = parseRecordDate(dateValue);
    return parsed.getTime() === 0 ? new Date().toISOString() : parsed.toISOString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) {
      return '-';
    }

    const parsed = parseRecordDate(dateStr);
    if (parsed.getTime() === 0) {
      return '-';
    }

    const dayPadded = String(parsed.getDate()).padStart(2, '0');
    const monthPadded = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = parsed.getFullYear();
    return `${dayPadded}/${monthPadded}/${year}`;
  };



  // Initialize with empty records for new user flow
  const [selectedMember, setSelectedMember] = useState({
    name: "User",
    records: []
  });
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingDate, setEditingDate] = useState('');
  const [pendingDeleteRecord, setPendingDeleteRecord] = useState(null);

  const [newRecord, setNewRecord] = useState({
    weight: '',
    neck: '',
    shoulders: '',
    chest: '',
    biceps: '',
    belly: '',
    waist: '',
    hip: '',
    calf: '',
    thighs: ''
  });
  const { user: authUser } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(true);

  const [openSection, setOpenSection] = useState(null);
  const [selectedMetrics, setSelectedMetrics] = useState(['weight']);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [timeRange, setTimeRange] = useState('30D');

  const parseCustomDate = (dStr) => {
    return parseRecordDate(dStr);
  };

  const toggleSection = async (section) => {
    if (section === 'progress') {
      // Refetch records when opening the progress section
      await fetchRecords().finally(() => setIsLoading(false));
    }

    setOpenSection((prev) => (prev === section ? null : section));
  };

  // ✅ Add this function near top of component
  const fetchRecords = async () => {
    // Already using authUser from context
    if (!authUser?.username || !authUser?.gymcode) return;

    try {
      // console.log('records request payload:', { Username: authUser.username });
      const response = await api.post('/api/FitnessTracking/records', { Username: authUser.username });
      const records = response.data;

      const cleanedRecords = records.map((r) => {
        const cleaned = {};
        for (const k in r) {
          if (k === "date") {
            cleaned[k] = formatDate(r[k]);
          } else {
            cleaned[k] = typeof r[k] === 'number' ? r[k] : parseFloat(r[k]);
          }
        }
        return cleaned;
      });

      if (cleanedRecords.length > 0) {
        setSelectedMember({
          name: authUser.firstName ? `${authUser.firstName} ${authUser.lastName || ''}` : "User",
          records: cleanedRecords
        });
        setShowWelcomeModal(false);
      } else {
        setSelectedMember({
          name: authUser.firstName ? `${authUser.firstName} ${authUser.lastName || ''}` : "User",
          records: []
        });
        setShowWelcomeModal(true);
      }
    } catch (err) {
      console.error('records error:', err.response?.data || err.message);
      setSelectedMember({
        name: authUser.firstName ? `${authUser.firstName} ${authUser.lastName || ''}` : "User",
        records: []
      });
      setShowWelcomeModal(true);
    }
  };

  // ✅ Update useEffect to use fetchRecords
  useEffect(() => {
    if (!authUser?.username || !authUser?.gymcode) return;
    fetchRecords().finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);



  const handleAddRecord = async () => {
    if (!authUser?.username) {
      console.warn('Cannot add record: user not authenticated');
      return;
    }
    const username = authUser.username;

    const recordData = {
      ...Object.fromEntries(
        Object.entries(newRecord).map(([key, val]) => [key, val ? parseFloat(val) : 0])
      ),
      Username: username,
      EnteredBy: username,
      Date: isEditing && editingDate ? editingDate : new Date().toISOString().split('T')[0],
      Active: true
    };

    if (isEditing) {
      try {
        if (authUser) {
          await api.put('/api/FitnessTracking/update', {
            ...recordData,
            id: editingId,
            Date: toApiDate(recordData.Date)
          });
        }

        // Update local state anyway for immediate feedback
        const updatedRecords = selectedMember.records.map(r =>
          r.id === editingId ? { ...r, ...recordData, date: formatDate(recordData.Date) } : r
        );
        setSelectedMember({ ...selectedMember, records: updatedRecords });
        setIsEditing(false);
        setEditingId(null);
        setEditingDate('');
      } catch (err) {
        console.error('Update failed:', err);
        // Fallback for demo
        const updatedRecords = selectedMember.records.map(r =>
          r.id === editingId ? { ...r, ...recordData, date: formatDate(recordData.Date) } : r
        );
        setSelectedMember({ ...selectedMember, records: updatedRecords });
        setIsEditing(false);
        setEditingId(null);
        setEditingDate('');
      }
    } else {
      try {
        let addedRecord = { ...recordData, id: Date.now(), date: formatDate(recordData.Date) };

        if (authUser) {
          const { data: serverAdded } = await api.post(
            '/api/FitnessTracking/AddFitnessTracking',
            {
              ...recordData,
              Date: new Date().toISOString()
            }
          );
          addedRecord = {
            ...serverAdded,
            date: formatDate(serverAdded.date || serverAdded.Date),
            weight: serverAdded.weight ?? serverAdded.Weight,
            id: serverAdded.id ?? serverAdded.Id
          };
        }

        const updated = { ...selectedMember, records: [...(selectedMember.records || []), addedRecord] };
        setSelectedMember(updated);
      } catch (err) {
        console.error('Add failed:', err);
        // Fallback for demo
        const demoAdded = { ...recordData, id: Date.now(), date: formatDate(recordData.Date) };
        setSelectedMember({ ...selectedMember, records: [...(selectedMember.records || []), demoAdded] });
      }
    }

    setNewRecord({
      weight: '', neck: '', shoulders: '', chest: '', biceps: '',
      belly: '', waist: '', hip: '', calf: '', thighs: ''
    });

    if (authUser) await fetchRecords().finally(() => setIsLoading(false));
    setOpenSection('progress');
  };

  const handleEditClick = (record) => {
    setNewRecord({
      weight: record.weight?.toString() || '',
      neck: record.neck?.toString() || '',
      shoulders: record.shoulders?.toString() || '',
      chest: record.chest?.toString() || '',
      biceps: record.biceps?.toString() || '',
      belly: record.belly?.toString() || '',
      waist: record.waist?.toString() || '',
      hip: record.hip?.toString() || '',
      calf: record.calf?.toString() || '',
      thighs: record.thighs?.toString() || ''
    });
    setIsEditing(true);
    setEditingId(record.id);
    setEditingDate(toDateInputValue(record.date));
    setOpenSection(null);
  };

  const handleDeleteRecord = async (id) => {
    try {
      if (authUser) {
        await fetch(`${API_BASE_URL}/api/FitnessTracking/${id}`, {
          method: "DELETE",
          headers: {
            'Authorization': `Bearer ${api.defaults.headers.common['Authorization']?.split(' ')[1] || '' || ''}`
          }
        });
      }

      setSelectedMember(prev => ({
        ...prev,
        records: prev.records.filter(r => r.id !== id)
      }));
    } catch (err) {
      console.error('Delete failed:', err);
      // Fallback for demo
      setSelectedMember(prev => ({
        ...prev,
        records: prev.records.filter(r => r.id !== id)
      }));
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingId(null);
    setEditingDate('');
    setNewRecord({
      weight: '', neck: '', shoulders: '', chest: '', biceps: '',
      belly: '', waist: '', hip: '', calf: '', thighs: ''
    });
  };






  // const handleMemberChange = async(e) => {


  //   if (!authUser) return;
  //   try {
  //     const username = authUser.username;

  //     const res = await fetch(`${API_BASE_URL}/api/FitnessTracking/records`,{
  //         headers: {
  //           'Content-Type': 'application/json',
  //         },
  //         body: JSON.stringify(username),
  //       }
  //     );

  //     const records = await res.json();
  //     const cleanedRecords = records.map((r) => {
  //         const cleaned = {};
  //         for (const k in r) {
  //             if (k === "date") {
  //               cleaned[k] = r[k];  // You format here sometimes
  //             } else {
  //               cleaned[k] = typeof r[k] === 'number' ? r[k] : parseFloat(r[k]);
  //             }
  //         }
  //         return cleaned;
  //     });


  //     const finalRecords = cleanedRecords.map((r) => ({

  //         ...r,
  //         date: r.date,

  //     }));

  //       setSelectedMember({ ...member, records: finalRecords });
  //       setOpenSection('addRecord');
  //     } catch (err) {
  //       console.error(err);
  //     }
  // };

  // Keys to track & display in graph and table
  const excludedKeys = ['id', 'date', 'Date', 'username', 'Username', 'enteredby', 'EnteredBy', 'active', 'Active'];
  const measurementKeys = Array.isArray(selectedMember?.records) && selectedMember.records.length > 0
    ? Object.keys(selectedMember.records[0]).filter(k => !excludedKeys.includes(k))
    : [];
  // Calculate dynamic summary stats
  const records = selectedMember?.records || [];
  let currentWeight = '-';
  let currentDateLabel = 'No data';
  let weightChange = null;
  let isGained = false;
  let isLost = false;

  if (records.length > 0) {
    const sortedRecords = [...records].sort((a, b) => parseCustomDate(a.date) - parseCustomDate(b.date));
    const latestRecord = sortedRecords[sortedRecords.length - 1];

    currentWeight = latestRecord.weight;

    const latestDate = parseCustomDate(latestRecord.date);
    const today = new Date();
    const isToday = latestDate.toDateString() === today.toDateString();
    const dateFormatted = latestDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    currentDateLabel = isToday ? `Today, ${dateFormatted}` : dateFormatted;

    const targetTime = latestDate.getTime() - (5 * 24 * 60 * 60 * 1000);
    let pastRecord = sortedRecords[0];
    let minDiff = Math.abs(parseCustomDate(pastRecord.date).getTime() - targetTime);

    for (let i = 1; i < sortedRecords.length - 1; i++) {
      const dTime = parseCustomDate(sortedRecords[i].date).getTime();
      const diff = Math.abs(dTime - targetTime);
      if (diff < minDiff) {
        minDiff = diff;
        pastRecord = sortedRecords[i];
      }
    }

    if (sortedRecords.length > 1) {
      const diff = latestRecord.weight - pastRecord.weight;
      weightChange = Math.abs(diff).toFixed(1);
      isGained = diff > 0;
      isLost = diff < 0;
    }
  }

  const getFilteredRecords = () => {
    if (!selectedMember?.records) return [];

    if (timeRange === 'Overall') {
      return [...selectedMember.records].sort((a, b) => parseCustomDate(a.date) - parseCustomDate(b.date));
    }

    const now = new Date();
    now.setHours(23, 59, 59, 999);

    let days = 30;
    if (timeRange === '7D') days = 7;
    else if (timeRange === '30D') days = 30;
    else if (timeRange === '90D') days = 90;

    const cutoff = new Date();
    cutoff.setDate(now.getDate() - days);
    cutoff.setHours(0, 0, 0, 0);

    return selectedMember.records.filter(record => {
      const recordDate = parseCustomDate(record.date);
      return recordDate >= cutoff;
    }).sort((a, b) => parseCustomDate(a.date) - parseCustomDate(b.date));
  };

  const filteredRecords = getFilteredRecords();

  return (
    isLoading ? (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6CD800]" />
      </div>
    ) :
      (
        <div className="space-y-6 w-full">
          <h1 className="text-3xl font-bold mb-6 text-center text-white">Fitness Tracking</h1>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 gap-4">
            {/* Card 1: Current Weight */}
            <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-4 relative overflow-hidden">
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Current Weight</span>
                <span className="text-[10px] text-gray-500">{currentDateLabel}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-3xl font-bold text-white tracking-tight">{currentWeight}</span>
                <span className="text-xs font-medium text-gray-500 mt-0.5">kg</span>
              </div>
            </div>

            {/* Card 2: 5-Day Change */}
            <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-4 flex flex-col justify-between relative overflow-hidden">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">5-Day Change</span>
              <div className="flex items-end justify-between w-full">
                <div className="flex flex-col">
                  <span className={`text-3xl font-bold tracking-tight ${isLost ? 'text-red-400' : isGained ? 'text-lime-400' : 'text-gray-400'}`}>
                    {weightChange !== null ? `${isGained ? '+' : isLost ? '-' : ''}${weightChange}` : '-'}
                  </span>
                  <span className={`text-xs font-medium mt-0.5 ${isLost ? 'text-red-500/80' : isGained ? 'text-lime-500/80' : 'text-gray-500/80'}`}>kg</span>
                </div>
                {weightChange !== null && (isGained || isLost) && (
                  <span className={`absolute bottom-2 right-2 text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-wider border ${isGained ? 'bg-lime-400/10 text-[#6CD800] border-lime-400/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
                    {isGained ? 'GAINED' : 'LOST'}
                  </span>
                )}
              </div>
            </div>
          </div>
          <AccordionSection
            title={selectedMember ? `Add Measurements for ${selectedMember.name}` : 'Add Measurements'} isOpen={openSection === 'addRecord'}
            onToggle={() => toggleSection('addRecord')}
          >
            {selectedMember ? (
              <div className="space-y-4 px-6">
                {Object.keys(newRecord).map((field) => (
                  <div key={field}>
                    <label className="block text-sm text-gray-400 capitalize mb-1">{field === 'weight' ? 'Weight (kg)' : `${field.charAt(0).toUpperCase() + field.slice(1)} (cm)`}</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={newRecord[field]}
                      onChange={(e) =>
                        setNewRecord({ ...newRecord, [field]: e.target.value })
                      }
                      onFocus={(e) => {
                        if (e.target.value === '0') {
                          setNewRecord({ ...newRecord, [field]: '' });
                        }
                      }}
                      className="w-full bg-[#222] border border-[#2b2b2b] rounded-lg px-4 py-2 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-lime-400"
                      placeholder={field === 'weight' ? '0.0 kg' : '0.0 cm'}
                    />
                  </div>
                ))}
                <div className="flex gap-4">
                  <button
                    onClick={handleAddRecord}
                    className="flex-1 bg-lime-400 text-black px-4 py-2 rounded-lg font-medium hover:bg-lime-500 transition"
                  >
                    {isEditing ? 'Update Entry' : 'Record Entry'}
                  </button>
                  {isEditing && (
                    <button
                      onClick={handleCancelEdit}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-400">Please select a member first.</p>
            )}
          </AccordionSection>

          <AccordionSection
            title={selectedMember ? `${selectedMember.name}'s Weight Progress` : 'Weight Progress'} isOpen={openSection === 'progress'}
            onToggle={() => toggleSection('progress')}
          >
            {selectedMember ? (
              <>
                <div className="w-full overflow-hidden">
                  {/* Controls Container */}
                  <div className="flex justify-between items-center gap-2 mb-6 px-4">
                    {/* Measurement Selector Dropdown (Left) */}
                    <div className="relative">
                      <button
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className="flex items-center gap-2 bg-[#161616] border border-gray-800 rounded-xl px-3 py-2 text-xs sm:text-sm font-medium text-white hover:bg-[#1a1a1a] transition-colors focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
                      >
                        <span
                          className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full"
                          style={{ backgroundColor: colors[selectedMetrics[0]] || '#8884d8' }}
                        />
                        <span className="truncate max-w-[60px] sm:max-w-none">
                          {selectedMetrics[0] ? selectedMetrics[0].charAt(0).toUpperCase() + selectedMetrics[0].slice(1) : 'Select'}
                        </span>
                        <svg
                          className={`w-3 h-3 sm:w-4 sm:h-4 ml-1 text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {isDropdownOpen && (
                        <div className="absolute left-0 mt-2 w-40 sm:w-48 bg-[#181818]/80 backdrop-blur-md border border-gray-800 rounded-xl shadow-xl overflow-hidden py-1 z-20">
                          {measurementKeys.map(key => (
                            <button
                              key={key}
                              onClick={() => {
                                setSelectedMetrics([key]);
                                setIsDropdownOpen(false);
                              }}
                              className={`w-full text-left px-4 py-2.5 text-xs sm:text-sm transition-colors flex items-center gap-2 ${selectedMetrics.includes(key)
                                ? 'bg-[#6CD800]/10 text-[#6CD800] font-medium'
                                : 'text-gray-300 hover:bg-[#222] hover:text-white'
                                }`}
                            >
                              <span
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: colors[key] || '#8884d8' }}
                              />
                              {key.charAt(0).toUpperCase() + key.slice(1)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Time Range Toggle (Right) */}
                    <div className="bg-[#161616] p-1 rounded-xl flex gap-1">
                      {['7D', '30D', '90D', 'Overall'].map((range) => (
                        <button
                          key={range}
                          onClick={() => setTimeRange(range)}
                          className={`px-2 sm:px-4 py-1.5 rounded-lg text-[10px] sm:text-xs font-bold transition-all duration-200 uppercase tracking-wider ${timeRange === range
                            ? 'bg-[#6CD800] text-black shadow-lg scale-105'
                            : 'text-gray-500 hover:text-white hover:bg-[#222]'
                            }`}
                        >
                          {range === 'Overall' ? 'ALL' : range}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="w-full aspect-video min-h-[300px] px-0 sm:px-6">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={filteredRecords}
                        margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#2b2b2b" vertical={false} />
                        <XAxis
                          dataKey="date"
                          tickFormatter={formatDate}
                          tick={{ fill: '#a1a1aa', fontSize: 12 }}
                          stroke="#2b2b2b"
                          minTickGap={15}
                          padding={{ left: 0, right: 0 }}
                        />
                        <YAxis
                          width={30}
                          tick={{ fill: '#a1a1aa', fontSize: 12 }}
                          stroke="#2b2b2b"
                          domain={['auto', 'auto']}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip contentStyle={{ backgroundColor: '#181818', borderColor: '#2b2b2b', borderRadius: '0.5rem', color: '#fff' }} />
                        <Legend wrapperStyle={{ color: 'white', paddingTop: '10px', fontSize: '12px' }} />
                        {measurementKeys.filter(key => selectedMetrics.includes(key)).map(key => (
                          <Line
                            key={key}
                            type="monotone"
                            dataKey={key}
                            stroke={colors[key] || '#8884d8'}
                            strokeWidth={3}
                            activeDot={{ r: 6, strokeWidth: 0 }}
                            name={key.charAt(0).toUpperCase() + key.slice(1)}
                            connectNulls
                            animationDuration={1000}
                            animationEasing="ease-in-out"
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>


                <div className="mt-6 overflow-x-auto rounded-lg border border-[#2b2b2b] mx-0 sm:mx-6">
                  <table className="min-w-full divide-y divide-[#2b2b2b] text-white">
                    <thead className="bg-[#222]">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Date</th>
                        {measurementKeys.map(key => (
                          <th key={key} className="px-4 py-2 text-left text-sm font-semibold capitalize" style={{ color: colors[key] || 'white' }}>{key}</th>
                        ))}
                        <th className="px-4 py-2 text-right text-sm font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#2b2b2b] bg-[#181818]">
                      {Array.isArray(selectedMember?.records) && selectedMember.records.map((record, idx) => (
                        <tr key={idx} className="hover:bg-[#2b2b2b]">
                          <td className="px-4 py-2 whitespace-nowrap">{formatDate(record.date)}</td>
                          {measurementKeys.map(key => (
                            <td key={key} className="px-4 py-2 whitespace-nowrap">
                              {record[key] != null && !isNaN(record[key]) ? record[key] : '-'}
                            </td>
                          ))}
                          <td className="px-4 py-2 whitespace-nowrap text-right space-x-2">
                            <button
                              onClick={() => handleEditClick(record)}
                              className="text-blue-400 hover:text-blue-300 text-xs font-medium"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => setPendingDeleteRecord(record)}
                              className="text-red-400 hover:text-red-300 text-xs font-medium"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p className="text-gray-400">Please select a member to view progress.</p>
            )}
          </AccordionSection>

          {isEditing && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
              <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-[#181818] border border-[#2b2b2b] rounded-xl p-6 shadow-2xl">
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div>
                    <h2 className="text-xl font-semibold text-white">Edit Weight Progress</h2>
                    <p className="text-sm text-gray-400 mt-1">Update the date and measurement values.</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="text-gray-400 hover:text-white text-2xl leading-none"
                    aria-label="Close edit dialog"
                  >
                    &times;
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Date</label>
                    <input
                      type="date"
                      value={editingDate}
                      onChange={(e) => setEditingDate(e.target.value)}
                      className="w-full bg-[#222] border border-[#2b2b2b] rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-1 focus:ring-lime-400"
                    />
                  </div>

                  {Object.keys(newRecord).map((field) => (
                    <div key={field}>
                      <label className="block text-sm text-gray-400 capitalize mb-1">
                        {field === 'weight' ? 'Weight (kg)' : `${field.charAt(0).toUpperCase() + field.slice(1)} (cm)`}
                      </label>
                      <input
                        type="number"
                        inputMode="decimal"
                        value={newRecord[field]}
                        onChange={(e) => setNewRecord({ ...newRecord, [field]: e.target.value })}
                        className="w-full bg-[#222] border border-[#2b2b2b] rounded-lg px-4 py-2 text-white placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-lime-400"
                        placeholder={field === 'weight' ? '0.0 kg' : '0.0 cm'}
                      />
                    </div>
                  ))}
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={handleAddRecord}
                    className="flex-1 bg-lime-400 text-black px-4 py-2 rounded-lg font-medium hover:bg-lime-500 transition"
                  >
                    Update
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="flex-1 bg-[#2b2b2b] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#333] transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {pendingDeleteRecord && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
              <div className="w-full max-w-sm bg-[#181818] border border-[#2b2b2b] rounded-xl p-6 shadow-2xl">
                <h2 className="text-lg font-semibold text-white mb-2">Delete Weight Progress?</h2>
                <p className="text-sm text-gray-400 mb-6">
                  Delete the entry from {formatDate(pendingDeleteRecord.date)}?
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={async () => {
                      const recordId = pendingDeleteRecord.id;
                      setPendingDeleteRecord(null);
                      await handleDeleteRecord(recordId);
                    }}
                    className="flex-1 bg-red-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-600 transition"
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingDeleteRecord(null)}
                    className="flex-1 bg-[#2b2b2b] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#333] transition"
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Welcome Modal for New Users */}
          {showWelcomeModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4">
              <div className="w-full max-w-md bg-[#181818] border border-[#2b2b2b] rounded-2xl p-6 shadow-2xl relative overflow-hidden animate-scale-up">

                {/* Visual Accent */}
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-lime-500 to-emerald-500" />

                {/* Modal Header Icon */}
                <div className="mx-auto w-12 h-12 bg-lime-400/10 rounded-full flex items-center justify-center text-lime-400 mb-4 border border-lime-400/20">
                  <svg className="w-6 h-6 animate-pulse" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 002 2h2a2 2 0 002-2z" />
                  </svg>
                </div>

                <div className="text-center space-y-4">
                  <h3 className="text-lg font-bold text-white tracking-wide">
                    Welcome to Fitness Tracking!
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Add your first weight or measurements to track your fitness.
                  </p>
                  <p className="text-lime-400 text-xs italic font-medium leading-relaxed bg-[#6CD800]/5 py-2 px-3 rounded-lg border border-[#6CD800]/10">
                    “You’re starting a journey, every small step counts.”
                  </p>

                  {/* Actions */}
                  <div className="pt-2 flex flex-col gap-2">
                    <button
                      onClick={() => {
                        setShowWelcomeModal(false);
                        setOpenSection('addRecord');
                      }}
                      className="w-full bg-lime-400 hover:bg-lime-300 text-[#181f10] font-bold py-2.5 px-4 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg transform active:scale-98 text-sm cursor-pointer"
                    >
                      Add Measurements Now
                    </button>
                    <button
                      onClick={() => setShowWelcomeModal(false)}
                      className="w-full bg-[#222] border border-[#2b2b2b] hover:border-gray-500 text-gray-300 hover:text-white font-semibold py-2 px-4 rounded-xl transition-all duration-200 text-xs cursor-pointer"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )
  );
};


export default HealthStats;
